import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../services/auth';

const TeacherLogin: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Vérifier si un enseignant est déjà connecté
  useEffect(() => {
    const existingTeacher = AuthService.getTeacher();
    if (existingTeacher) {
      navigate('/teacher/dashboard');
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation des champs requis
    if (!email.trim()) {
      setError('Veuillez saisir votre email');
      return;
    }

    if (!password.trim()) {
      setError('Veuillez saisir votre mot de passe');
      return;
    }

    if (isRegisterMode) {
      if (!name.trim()) {
        setError('Veuillez saisir votre nom');
        return;
      }

      if (password.length < 6) {
        setError('Le mot de passe doit contenir au moins 6 caractères');
        return;
      }

      if (password !== confirmPassword) {
        setError('Les mots de passe ne correspondent pas');
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      let response;
      
      if (isRegisterMode) {
        // Inscription
        response = await AuthService.registerTeacher(name.trim(), email.trim(), password);
        
        if (response.success) {
          // Sauvegarder les informations de l'enseignant
          AuthService.saveTeacher(response.teacher);
          
          // Afficher le message de succès
          setShowSuccess(true);
          
          // Rediriger vers le tableau de bord après un délai
          setTimeout(() => {
            navigate('/teacher/dashboard');
          }, 2000);
        }
      } else {
        // Connexion
        response = await AuthService.loginTeacher(email.trim(), password);
        
        if (response.success) {
          // Sauvegarder les informations de l'enseignant
          AuthService.saveTeacher(response.teacher);
          
          // Rediriger vers le tableau de bord
          navigate('/teacher/dashboard');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'authentification');
    } finally {
      setLoading(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="login-container">
        <div className="login-background">
          <div className="login-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>
          </div>
        </div>
        
        <div className="login-content">
          <div className="welcome-card">
            <div className="welcome-icon">
              <div className="icon-circle success">
                <i className="fas fa-user-plus"></i>
              </div>
            </div>
            <h2 className="welcome-title">Bienvenue !</h2>
            <p className="welcome-text">
              Votre compte enseignant a été créé avec succès.
            </p>
            <div className="teacher-info">
              <span className="teacher-label">Nom:</span>
              <span className="teacher-name">{name}</span>
            </div>
            <div className="redirect-message">
              <div className="loading-spinner"></div>
              <span>Redirection vers votre tableau de bord...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="login-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
      </div>
      
      <div className="login-content">
        <div className="login-card">
          <div className="login-header">
            <div className="login-icon">
              <div className="icon-circle primary">
                <i className="fas fa-chalkboard-teacher"></i>
              </div>
            </div>
            <h1 className="login-title">
              {isRegisterMode ? 'Inscription Enseignant' : 'Connexion Enseignant'}
            </h1>
            <p className="login-subtitle">
              {isRegisterMode 
                ? 'Créez votre compte pour accéder à l\'espace d\'enseignement'
                : 'Accédez à votre espace d\'enseignement interactif'
              }
            </p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            {isRegisterMode && (
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  <i className="fas fa-user"></i>
                  <span>Nom complet</span>
                  <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Entrez votre nom complet"
                  required
                  disabled={loading}
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email" className="form-label">
                <i className="fas fa-envelope"></i>
                <span>Email</span>
                <span className="required">*</span>
              </label>
              <input
                type="email"
                className="form-input"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre.email@exemple.com"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                <i className="fas fa-lock"></i>
                <span>Mot de passe</span>
                <span className="required">*</span>
              </label>
              <input
                type="password"
                className="form-input"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isRegisterMode ? "Au moins 6 caractères" : "Votre mot de passe"}
                required
                disabled={loading}
              />
              {isRegisterMode && (
                <div className="form-hint">
                  Le mot de passe doit contenir au moins 6 caractères.
                </div>
              )}
            </div>

            {isRegisterMode && (
              <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">
                  <i className="fas fa-lock"></i>
                  <span>Confirmer le mot de passe</span>
                  <span className="required">*</span>
                </label>
                <input
                  type="password"
                  className="form-input"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirmez votre mot de passe"
                  required
                  disabled={loading}
                />
              </div>
            )}

            {error && (
              <div className="error-message">
                <i className="fas fa-exclamation-triangle"></i>
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              className="login-button"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="button-spinner"></div>
                  <span>{isRegisterMode ? 'Inscription en cours...' : 'Connexion en cours...'}</span>
                </>
              ) : (
                <>
                  <i className={isRegisterMode ? 'fas fa-user-plus' : 'fas fa-sign-in-alt'}></i>
                  <span>{isRegisterMode ? 'S\'inscrire' : 'Se connecter'}</span>
                </>
              )}
            </button>
          </form>

          <div className="login-footer">
            <div className="info-message">
              <i className="fas fa-info-circle"></i>
              <span>
                {isRegisterMode 
                  ? "Créez votre compte pour commencer à utiliser l'application."
                  : 'Connectez-vous avec votre email et mot de passe.'
                }
              </span>
            </div>
            
            <button
              type="button"
              onClick={() => {
                setIsRegisterMode(!isRegisterMode);
                setError(null);
                setName('');
                setEmail('');
                setPassword('');
                setConfirmPassword('');
              }}
              className="back-button"
            >
              <i className={isRegisterMode ? 'fas fa-sign-in-alt' : 'fas fa-user-plus'}></i>
              <span>
                {isRegisterMode 
                  ? 'Déjà un compte ? Se connecter'
                  : 'Nouveau ? Créer un compte'
                }
              </span>
            </button>
            
            <button
              onClick={() => navigate('/')}
              className="back-button"
            >
              <i className="fas fa-arrow-left"></i>
              <span>Retour à l'accueil</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherLogin;
